<template>
  <BbPopup v-model="confirmPopupOpened" :showCloseIcon="true" contentClass="layouts-list-popup">
    <template #popup-header>
      <TextBody tag="span" i18n-key="author.pickLayout" :body-type="3" weight="medium" />
      <BbInput v-model="filterValue" class="search-input" size="small" :placeholder="t('common.search')" autofocus>
        <template #prepend-inner>
          <BbIcon size="small" name="search" />
        </template>
      </BbInput>
    </template>
    <TextBody v-if="!filteredGroupedLayouts.length" i18n-key="common.noDataText" tag="span" :body-type="1" />
    <BbTreeview
      v-for="group in filteredGroupedLayouts"
      :key="group.name"
      :title="group.name"
      hide-icon
      initial-state
      class="treeview-group"
    >
      <BbTreeviewItem v-for="layout in group.layouts" :key="layout.id">
        <BbDashboardCard class="amp-card" fill-width>
          <template #image>
            <div v-if="getThumbnailUrl(layout.id)" class="thumbnail-wrapper" @click="onLayoutClick(layout)">
              <BaseImage :src="getThumbnailUrl(layout.id)" alt="layout thumbnail" @error="onImageError(layout.id)" />
            </div>
            <div v-else class="missing-thumbnail-wrapper" @click="onLayoutClick(layout)">
              <BbIcon class="icon" name="medialibrary" />
            </div>
          </template>
          <div class="bb-dashboard-card__title_container" data-app>
            <div class="bb-dashboard-card__title hover-active">{{ layout.name }}</div>
          </div>
        </BbDashboardCard>
      </BbTreeviewItem>
    </BbTreeview>
  </BbPopup>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { groupBy, debounce } from 'lodash';
import { useI18n } from 'vue-i18n';

import TextBody from '~/components/common/TextBody';
import BaseImage from '~/components/common/BaseImage.vue';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useModals from '~/composables/useModals';
import useConversations from '~/composables/useConversations';
import layoutThumbnailCache from '~/services/thumbnail-cache-service/layout-thumbnail-cache-utils';

export default {
  components: { TextBody, BaseImage },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { getLayoutsByParentId, currentTemplate } = useDocumentEditor();
    const { currentConversation } = useConversations();
    const { layoutsListOpened, setLayoutsListOpened } = useModals();

    const availableLayouts = ref([]);
    const filterValue = ref('');
    const thumbnailUrls = ref({});

    const confirmPopupOpened = computed({
      get: () => layoutsListOpened.value,
      set: (val) => setLayoutsListOpened(val),
    });

    const getThumbnailUrl = (layoutId) => {
      // Access reactive ref to trigger re-render when thumbnails are refreshed
      const url = thumbnailUrls.value[layoutId];
      return layoutThumbnailCache.get(layoutId);
    };

    const groupedAvailableLayouts = computed(() => {
      const grouped = groupBy(availableLayouts.value, (layout) => layout.templateName.split('/')[0]);

      return Object.keys(grouped)
        .filter((group) => grouped[group].length > 0)
        .map((group) => ({
          name: group,
          layouts: grouped[group].map((layout) => ({
            id: layout.id,
            name: layout.templateName.split('/')[1],
          })),
        }));
    });

    const filteredGroupedLayouts = computed(() => {
      const query = filterValue.value.trim().toLowerCase();
      if (!query) {
        return groupedAvailableLayouts.value;
      }

      return groupedAvailableLayouts.value
        .map((group) => {
          const matchesGroup = group.name.toLowerCase().includes(query);
          const filteredLayouts = group.layouts.filter((layout) => layout.name.toLowerCase().includes(query));
          if (matchesGroup || filteredLayouts.length > 0) {
            return {
              ...group,
              layouts: matchesGroup ? group.layouts : filteredLayouts,
            };
          }
          return null;
        })
        .filter(Boolean);
    });

    const onLayoutClick = (layout) => {
      emit('layout-selected', layout);
      setLayoutsListOpened(false);
    };

    let pendingRefreshIds = new Set();
    let isRefreshing = false;

    const refreshExpiredThumbnails = debounce(async () => {
      if (isRefreshing || pendingRefreshIds.size === 0) {
        return;
      }

      isRefreshing = true;
      pendingRefreshIds.clear();

      const parentId = currentTemplate.value?.parentId || currentConversation.value?.designSystemId;

      try {
        const freshLayouts = await getLayoutsByParentId(parentId, true);

        const newUrls = { ...thumbnailUrls.value };
        freshLayouts.forEach((layout) => {
          if (layout.url) {
            layoutThumbnailCache.set(layout.id, layout.url);
            newUrls[layout.id] = layout.url;
          }
        });
        thumbnailUrls.value = newUrls;
      } catch (error) {
        console.error('Failed to refresh thumbnails:', error);
      } finally {
        isRefreshing = false;
        if (pendingRefreshIds.size > 0) {
          refreshExpiredThumbnails();
        }
      }
    }, 300);

    const onImageError = (layoutId) => {
      layoutThumbnailCache.remove(layoutId);
      pendingRefreshIds.add(layoutId);
      refreshExpiredThumbnails();
    };

    watch(layoutsListOpened, async (newVal) => {
      if (!newVal) return;

      const parentId = currentTemplate.value?.parentId || currentConversation.value?.designSystemId;

      if (!availableLayouts.value.length) {
        const layouts = await getLayoutsByParentId(parentId, true);

        const newUrls = {};
        layouts.forEach((layout) => {
          if (layout.url) {
            layoutThumbnailCache.set(layout.id, layout.url);
            newUrls[layout.id] = layout.url;
          }
        });
        thumbnailUrls.value = newUrls;
        availableLayouts.value = layouts;
      }
    });

    return {
      t,
      confirmPopupOpened,
      filteredGroupedLayouts,
      onLayoutClick,
      filterValue,
      getThumbnailUrl,
      onImageError,
    };
  },
};
</script>

<style scoped lang="scss">
.amp-card {
  width: 215px !important;
  height: 128px !important;

  :deep(.bb-dashboard-card__content) {
    padding: $bb-spacing-2;
  }

  :deep(.bb-dashboard-card__title) {
    font-size: 11px;
    color: $bb-neutral-2;
  }

  .missing-thumbnail-wrapper {
    width: 100% !important;
    background: $bb-interface-4 !important;

    .icon {
      font-size: 50px;
      color: $bb-interface-7;
    }
  }
}

.treeview-group {
  width: 100% !important;

  :deep(.treeview-item) {
    display: flex;
    flex-wrap: wrap;
    column-gap: $bb-spacing-2;
    row-gap: $bb-spacing-2;
  }

  :deep(.bb-treeview-item) {
    padding: 0 !important;
  }

  :deep(.bb-treeview__button) {
    margin: $bb-spacing-4 0;
  }
}

.search-input {
  width: 50%;
  @include bb-medium-text-styles('sm');

  :deep(.v-input) {
    .v-input__control {
      .v-field {
        .v-field__outline__start,
        .v-field__outline__end {
          border-color: transparent !important;
        }
      }
    }
  }
}
</style>
